<template>
  <b-modal 
    id="modal-add-participant" 
    centered 
    hide-footer 
    title="Tambah Peserta"
    size="lg">
    <form @submit.prevent="saveAdd()">
      <div class="row">
        <div class="col-md-12">
          <div class="form-group">
            <label>Cari karyawan berdasarkan NIK atau nama <span class="text-danger">*</span></label>
            <v-select
              v-validate="'required'"
              name="Employee"
              v-model="selectedEmployee"
              :options="searchedEmployees"
              :reduce="option => option"
              label="employee_name"
              placeholder="Cari NIK atau nama..."
              @search="onSearchEmployee"
              @input="onEmployeeSelected"
              :filterable="false"
              :loading="isSearching">
              <template #no-options="{ searching }">
                <template v-if="!searching">
                  Ketik minimal 2 karakter untuk mencari.
                </template>
                <template v-else-if="searchedEmployees.length === 0 && searching">
                  Karyawan tidak ditemukan.
                </template>
                <em v-else>Cari nama atau NIK</em>
              </template>
              <template #option="option">
                <div>
                  <div class="font-weight-bold">{{ option.fullname }} | {{ option.nik }}</div>
                </div>
                <div class="d-flex align-items-center">
                  <div>
                    <small>NIK: {{ option.nik }} | Jabatan: {{ option.position }}</small>
                  </div>
                </div>
              </template>
            </v-select>
            <span
              class="text-sm text-danger"
              v-show="errors.has('Employee')"
              >{{ errors.first("Employee") }}</span>
            <div
              v-if="syncFeedback.message"
              class="alert mt-2 mb-0 py-2"
              :class="syncFeedback.className">
              <span
                v-if="syncFeedback.loading"
                class="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"></span>
              {{ syncFeedback.message }}
            </div>
            <small class="text-muted d-block mt-2">
              Peserta hanya bisa dipilih untuk salah satu tahun planning. Jika sudah terdaftar di tahun lain, peserta tidak bisa ditambahkan lagi.
            </small>
          </div>
        </div>

        <!-- Employee Card -->
        <div v-if="selectedEmployee" class="col-md-12">
          <div class="card mb-3">
            <div class="card-body">
              <div class="d-flex align-items-start">
                <div class="user-avatar me-3">
                  <div class="avatar-circle">
                    <i class="fe fe-user"></i>
                  </div>
                </div>
                <div class="flex-grow-1">
                  <div class="d-flex justify-content-between align-items-start">
                    <div>
                      <h5 class="mb-1">{{ selectedEmployee.employee_name }}</h5>
                      <p class="text-muted mb-2">{{ selectedEmployee.position }}</p>
                    </div>
                    <span class="badge badge-primary">{{ selectedEmployee.employee_id }}</span>
                  </div>
                  <div class="user-details mt-3">
                    <div class="row">
                      <div class="col-md-6">
                        <div class="detail-item">
                          <strong>NIK</strong>
                          <p>{{ selectedEmployee.employee_id }}</p>
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="detail-item">
                          <strong>Jabatan</strong>
                          <p>{{ selectedEmployee.position || '-' }}</p>
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="detail-item">
                          <strong>EMAIL</strong>
                          <p>{{ selectedEmployee.email || '-' }}</p>
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="detail-item">
                          <strong>Perusahaan</strong>
                          <p>{{ selectedEmployee.company || '-' }}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="form-group text-right mt-4">
        <button
          type="button"
          class="btn btn-secondary me-2"
          @click="closeModal">
          Batal
        </button>
        <button
          class="btn btn-danger"
          type="submit"
          :disabled="isAdding || !selectedEmployee">
          <span
            v-if="isAdding"
            class="spinner-border spinner-border-sm me-2"
            role="status"
            aria-hidden="true"></span>
          <i class="fe fe-plus me-1"></i>
          Tambah Peserta
        </button>
      </div>
    </form>
  </b-modal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { normalizeErrorMessage } from '@/utils/error-message';

export default {
  name: 'ModalAddParticipant',
  props: {
    courseId: {
      type: [Number, String],
      default: null
    },
    planningYear: {
      type: Number,
      default: null
    },
    selectedCompany: {
      type: String,
      default: null
    }
  },
  computed: {
    ...mapGetters('tnaMitra', {
      searchedEmployees: 'searchedEmployees',
      participants: 'participants'
    })
  },
  data() {
    return {
      selectedEmployee: null,
      searchQuery: '',
      isAdding: false,
      isSearching: false,
      searchTimer: null,
      searchRequestId: 0,
      syncFeedback: {
        loading: false,
        message: '',
        className: 'alert-info'
      }
    }
  },
  methods: {
    ...mapActions('tnaMitra', ['searchEmployees', 'syncEmployeeOnDemand', 'addParticipant']),

    normalizeParticipantYear(participant) {
      const fallbackYear = new Date().getFullYear();
      const parsedYear = Number(
        participant?.planning_year || participant?.year || participant?.training_year
      );

      if (Number.isInteger(parsedYear) && parsedYear > 0) {
        return parsedYear;
      }

      return fallbackYear;
    },

    getSelectedPlanningYear() {
      const fallbackYear = new Date().getFullYear();
      if (Number.isInteger(this.planningYear) && this.planningYear > 0) {
        return this.planningYear;
      }

      return fallbackYear;
    },
    
    onSearchEmployee(search) {
      clearTimeout(this.searchTimer);
      const requestId = ++this.searchRequestId;
      this.syncFeedback = { loading: false, message: '', className: 'alert-info' };

      if (search.length >= 2) {
        this.searchQuery = search;
        this.isSearching = true;
        this.searchTimer = setTimeout(() => this.performSearch(search, requestId), 400);
      } else {
        this.searchQuery = '';
        this.isSearching = false;
      }
    },

    async performSearch(search, requestId) {
      try {
        const params = this.selectedCompany ? { company: this.selectedCompany } : {};
        const employees = await this.searchEmployees({
          search,
          params
        });
        if (requestId !== this.searchRequestId) return;

        const exactNikFound = employees.some(
          employee => this.normalizeNik(employee.employee_id || employee.nik) === this.normalizeNik(search)
        );

        if (!exactNikFound && this.looksLikeNik(search)) {
          this.syncFeedback = {
            loading: true,
            message: 'NIK tidak ditemukan di lokal. Memeriksa Portal...',
            className: 'alert-info'
          };

          const result = await this.syncEmployeeOnDemand({
            nik: search.trim(),
            params
          });
          if (requestId !== this.searchRequestId) return;

          if (result?.employee) {
            const syncedEmployee = (result.employees || []).find(
              employee => this.normalizeNik(employee.employee_id || employee.nik) === this.normalizeNik(search)
            );

            if (syncedEmployee) {
              this.selectedEmployee = syncedEmployee;
              this.syncFeedback = { loading: false, message: '', className: 'alert-info' };
              return;
            }
          }

          const feedbackClass = ['inactive', 'not_syncable', 'outside_scope'].includes(result?.status)
            ? 'alert-warning'
            : 'alert-info';

          this.syncFeedback = {
            loading: false,
            message: result?.message || 'Sinkronisasi user selesai.',
            className: feedbackClass
          };
        }
      } catch (error) {
        console.error('Error searching employees:', error);
        if (requestId === this.searchRequestId) {
          this.syncFeedback = {
            loading: false,
            message: error.response?.data?.message || 'Pencarian user belum berhasil. Silakan coba kembali.',
            className: 'alert-danger'
          };
        }
      } finally {
        if (requestId === this.searchRequestId) {
          this.isSearching = false;
        }
      }
    },

    normalizeNik(value) {
      return String(value || '').trim().toUpperCase();
    },

    looksLikeNik(value) {
      return /^(?:[A-Z]+-)?\d{4,}$/i.test(String(value || '').trim());
    },

    onEmployeeSelected(employee) {
      console.log('✅ Employee selected:', employee);
      this.selectedEmployee = employee;
    },

    getExistingParticipantYear(employee) {
      const existingParticipant = this.participants.find(p =>
        p.employee_id === employee.employee_id
        || p.nik === employee.nik
        || p.id === employee.id
      );

      if (!existingParticipant) {
        return null;
      }

      return this.normalizeParticipantYear(existingParticipant);
    },

    async saveAdd() {
      if (!this.selectedEmployee) {
        this.$swal('Data Belum Dipilih', 'Pilih karyawan terlebih dahulu.', 'warning');
        return;
      }

      const existingYear = this.getExistingParticipantYear(this.selectedEmployee);
      if (existingYear) {
        this.$swal(
          'Warning',
          `Karyawan sudah terdaftar sebagai peserta pada planning year ${existingYear}. Peserta hanya bisa berada di satu tahun planning.`,
          'warning'
        );
        return;
      }

      this.isAdding = true;

      try {
        // Add participant to store only (no API call)
        this.addParticipant({
          participant: this.selectedEmployee,
          planningYear: this.getSelectedPlanningYear()
        });

        this.$swal({
          title: 'Success',
          text: `${this.selectedEmployee.employee_name} berhasil ditambahkan sebagai peserta.`,
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });

        // Reset and close
        this.resetForm();
        this.closeModal();
      } catch (error) {
        console.error('Error adding participant:', error);
        this.$swal('Gagal Menambah Peserta', normalizeErrorMessage(error, 'Peserta belum berhasil ditambahkan.'), 'error');
      } finally {
        this.isAdding = false;
      }
    },

    resetForm() {
      clearTimeout(this.searchTimer);
      this.searchRequestId += 1;
      this.selectedEmployee = null;
      this.searchQuery = '';
      this.isSearching = false;
      this.syncFeedback = { loading: false, message: '', className: 'alert-info' };
    },

    closeModal() {
      this.resetForm();
      this.$bvModal.hide('modal-add-participant');
    }
  },

  beforeDestroy() {
    clearTimeout(this.searchTimer);
  }
}
</script>

<style scoped>
.user-avatar {
  flex-shrink: 0;
}

.user-avatar .avatar-circle {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
}

.user-details .detail-item {
  margin-bottom: 1rem;
}

.user-details .detail-item strong {
  display: block;
  font-size: 0.75rem;
  color: #6c757d;
  text-transform: uppercase;
  margin-bottom: 0.25rem;
}

.user-details .detail-item p {
  margin: 0;
  font-size: 0.9rem;
  color: #212529;
}

.me-2 {
  margin-right: 0.5rem !important;
}

.me-3 {
  margin-right: 1rem !important;
}

.card {
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
}

.badge-primary {
  background-color: #2c7be5;
}
</style>
